import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

interface BillDocumentPreviewResponse {
  preview_text: string
  truncated: boolean
}

interface GetBillDocumentPreviewParams {
  billId: number
  organizationId: Organization["id"]
}

const getBillDocumentPreview = async ({
  billId,
  organizationId,
}: GetBillDocumentPreviewParams): Promise<BillDocumentPreviewResponse> => {
  try {
    const response = await httpClient.get<BillDocumentPreviewResponse>(
      `/api/v1/organizations/${organizationId}/bills/${billId}/document_preview`
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseBillDocumentPreviewOptions {
  billId: number | null
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<BillDocumentPreviewResponse, HttpError>,
    "queryKey" | "queryFn"
  >
}

export const useBillDocumentPreview = ({
  billId,
  organizationId,
  queryOptions = {},
}: UseBillDocumentPreviewOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.BillDocument,
    "getOne",
    billId === null ? null : String(billId),
    {
      endpoint: "document_preview",
      organizationId,
    }
  )

  const queryResult = useQuery<BillDocumentPreviewResponse, HttpError>({
    ...queryOptions,
    enabled:
      Boolean(organizationId) &&
      billId !== null &&
      (queryOptions.enabled ?? true),
    queryFn: async () => {
      if (billId === null) {
        throw new Error("billId is required to fetch bill document preview")
      }
      return getBillDocumentPreview({ billId, organizationId })
    },
    queryKey,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.BillDocument)
}
